<template>
    <div class="venue-registration-container">
        <div class="venue-registration-wrapper">
            <div id="preloader" v-if="load" class="preloader"></div>
            <Stepper
                :steps="steps"
                :initStepIndx="initStep"
                @registrationComplete="handleSubmit"
            />
            <div v-if="serverErrMsg.length" class="server-error">
                {{ serverErrMsg }}
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import Stepper from '@/components/registration/parts/Stepper.vue'
    import RegistrationForm from './steps/RegistrationForm.vue'
    import FillTheGaps from './steps/FillTheGaps.vue'
    import SocialRegistration from './steps/SocialRegistration.vue'

    const URLS = {
        xhr: {
            fetch: {
                checkAuth: '/check-authorization',
                user: '/profile/v1/settings/user',
                venueInfo: '/profile/v1/user-profile/venue-info'
            },
            mutate: {
                social: ['/profile/social/link', 'role/venue'],
                media: ['/profile/update-media', 'venue'],
                venueInfo: '/profile/v1/info/venue'
            }
        },
        redirect: {
            venueProfile: '/venues'
        }
    }

    export default {
        name: 'VenueProfileRegistration',

        components: {
            Stepper
        },

        data: () => ({
            steps: [
                {
                    title: 'REGISTER',
                    component: RegistrationForm,
                    props: {
                        mode: 'profileCreation'
                    }
                },
                {
                    title: 'ADD SOCIALS',
                    component: SocialRegistration
                },
                {
                    title: 'FILL THE GAPS',
                    component: FillTheGaps
                }
            ],

            load: false,
            authedUser: false,
            initStep: 0,
            serverErrMsg: '',
            shouldRedirect: true
        }),

        methods: {
            prepareSocialLink(data) {
                return Object.keys(data)
                    .filter((key) => key !== 'website' && data[key] !== '')
                    .reduce((obj, key) => {
                        obj[key] = data[key]
                        return obj
                    }, {})
            },

            prepareMediaUpload(data) {
                let formData = new FormData()

                for (let i = 0; i < data.medias.length; i++) {
                    formData.append('files[]', data.medias[i])
                }

                formData.append('_method', 'PUT')
                return formData
            },

            prepareVenueProfileUpdate(data) {
                let preparedData = {}

                if (data['ADD SOCIALS'].website) {
                    preparedData.web_site = data['ADD SOCIALS'].website
                }
                if (data['FILL THE GAPS'].location) {
                    preparedData.address = data['FILL THE GAPS'].location
                }
                if (data['FILL THE GAPS'].lat && data['FILL THE GAPS'].lng) {
                    preparedData.lon = parseFloat(data['FILL THE GAPS'].lng)
                    preparedData.lat = parseFloat(data['FILL THE GAPS'].lat)
                }

                preparedData.venue_name = data['FILL THE GAPS'].venueName

                return preparedData
            },

            initPostData(data) {
                let postData = {}
                let socialLinkData = this.prepareSocialLink(data['ADD SOCIALS'])
                let mediaUploadData = this.prepareMediaUpload(data['FILL THE GAPS'])
                let venueUpdateData = this.prepareVenueProfileUpdate(data)

                if (Object.keys(socialLinkData).length) {
                    postData.socialLinks = socialLinkData
                }
                if (mediaUploadData) {
                    postData.uploadMedias = mediaUploadData
                }
                if (Object.keys(venueUpdateData).length) {
                    postData.venueUpdate = venueUpdateData
                }

                return postData
            },

            async updateSocialLinks(userId, data) {
                try {
                    const response = await axios.put(
                        `${this.URLS.xhr.mutate.social[0]}/${userId}/${this.URLS.xhr.mutate.social[1]}`,
                        data
                    )
                    return response
                } catch (err) {
                    this.serverErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`
                    throw err
                }
            },

            async updateMedias(userId, data) {
                try {
                    const response = await axios.post(
                        `${this.URLS.xhr.mutate.media[0]}/${userId}/${this.URLS.xhr.mutate.media[1]}`,
                        data
                    )
                    return response
                } catch (err) {
                    this.serverErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`
                    throw err
                }
            },

            async updateVenueInfo(userId, data) {
                try {
                    const response = await axios.post(
                        `${this.URLS.xhr.mutate.venueInfo}/${userId}`,
                        {
                            _method: 'PUT',
                            ...data
                        }
                    )
                    return response
                } catch (err) {
                    this.serverErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`
                    throw err
                }
            },

            async handleSubmit(data) {
                let postData = this.initPostData(data)
                let userResp = await axios.get(this.URLS.xhr.fetch.user)
                let userId = userResp.data.data[0].id
                let socialLinkResp, uploadMediaResp, venueUpdateResp

                this.shouldRedirect = true
                this.load = true

                try {
                    if (postData.socialLinks) {
                        socialLinkResp = await this.updateSocialLinks(
                            userId,
                            postData.socialLinks
                        )
                    }

                    uploadMediaResp = await this.updateMedias(userId, postData.uploadMedias)

                    if (postData.venueUpdate) {
                        venueUpdateResp = await this.updateVenueInfo(
                            userId,
                            postData.venueUpdate
                        )
                    }

                    if (this.shouldRedirect) {
                        let venueResp = await axios.get(
                            `${this.URLS.xhr.fetch.venueInfo}/${userId}`
                        )
                        let venueSlug = venueResp.data.data.slug
                        window.location.href = `${this.URLS.redirect.venueProfile}/${venueSlug}`
                    }

                    return {
                        socialLinkResp,
                        uploadMediaResp,
                        venueUpdateResp
                    }
                } catch (err) {
                    this.shouldRedirect = false
                } finally {
                    this.load = false
                }
            },

            async isAuthed() {
                try {
                    const resp = await axios.get(this.URLS.xhr.fetch.checkAuth)

                    if (resp?.data?.isAuthorized) {
                        this.authedUser = true
                    }
                } catch {
                    this.authedUser = false
                }
            }
        },

        computed: {
            URLS: () => URLS
        },

        async created() {
            await this.isAuthed()
            this.authedUser ? (this.initStep = 1) : (this.initStep = 0)
        }
    }
</script>

<style lang="scss" scoped>
    .venue-registration-container {
        display: flex;
        flex-direction: row;
        justify-content: center;

        .venue-registration-wrapper {
            max-width: 1440px;
            width: 100%;
            background-color: #121212;

            input:-webkit-autofill,
            input:-webkit-autofill:hover,
            input:-webkit-autofill:focus,
            input:-webkit-autofill:active {
                transition: background-color 5000s ease-in-out 0s;
                -webkit-text-fill-color: #fff !important;
            }

            .server-error {
                color: #dc3545;
                position: relative;
                bottom: 50px;
                text-align: center;
            }
        }
    }

    .pac-container {
        background: transparent;
        border: none;
    }
    .pac-icon {
        background-image: url(/images/icons/marker.png);
    }

    .pac-item {
        color: white;
    }
    .pac-item:hover {
        color: #d7eb5a;
        background: transparent;
        font-weight: bolder;
    }
    .pac-item-selected {
        color: #d7eb5a;
        background: transparent;
        font-weight: bolder;
    }
    .pac-item-query {
        background: transparent;
    }
    .pac-matched {
        color: #d7eb5a;
        font-weight: bolder;
    }
    .pac-item-selected .pac-icon-marker {
        background-position: center;
    }
</style>
